import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 主な活動ページ 全面作り直し（Implementer A スコープ） =====
// デザイン土台: src/pages/about/overview/page.tsx（型の正典）と src/pages/home/page.tsx。
//   紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / リード3行+ / BTN-08 2状態 / 金eyebrow+和見出し。
//   セクション型は1ページ内で全て別型。overviewの型（定義リスト/引用大組み/縦タイムライン）の
//   単純コピーはせず、学習した文法から本ページに合う新バリエーションを設計。
// 事実正典（創作禁止・出典path記録。正典に無い年号/数値は書かない。外部実在との関わり主張は禁止）:
//   [A1] mng-thebutler/00_事業説明.md §4-3 L63（Associate/Professional/Master・約3年育成・全国約50名規模）
//   [A2] mng-thebutler/00_事業説明.md §4-4 L64（双方向NDA・反社会的勢力チェック）
//   [A3] mng-thebutler/00_事業説明.md §4-5 L65（派遣でない業務委託・協会が責任）
//   [A4] mng-thebutler/00_事業説明.md §1 L22-27（理念: 時間と心の余裕/家の心臓/先回りの奉仕）
//   [A5] src/pages/about/overview/page.tsx businessFields（5事業領域: 人材育成/資格認定/セミナー/国際交流/出版）
//   [A6] mng-thebutler/00_事業説明.md §3-2 L52-55, §7 L96（提供内容・YouTube「執事ベル」）

export default function ActivitiesPage() {
  useEffect(() => {
    updateSEO(seoData['/about/activities']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について', href: '/about' },
    { label: '主な活動' }
  ];

  const aboutNav = [
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [A5][A6] 活動の全体像（大フォント一覧・可読性優先）。数値は創作しない。
  const activityPillars = [
    { no: '01', name: '人材育成と研修', lead: '基礎から専門技術まで、体系的な研修で質の高い執事を育てます。', anchor: '#training' },
    { no: '02', name: '資格認定制度', lead: 'アソシエイト・プロフェッショナル・マスターの三段階で技能を証明します。', anchor: '#certification' },
    { no: '03', name: '学びを社会へ広げる', lead: 'セミナー・国際交流・出版を通じ、執事文化を社会へ届けます。', anchor: '#outreach' },
    { no: '04', name: '信頼と品質を支える仕組み', lead: '守秘・審査・責任構造で、安心してお任せいただける体制を整えます。', anchor: '#trust' },
  ];

  // [A1] 三段階資格（積層カード・大フォント資格名）
  const certifications = [
    { grade: 'Associate', ja: 'アソシエイト・バトラー', img: '/images/generated/jba-still-shipdesk-01.webp', alt: '帆船模型と地球儀の学びの机', desc: '執事としての基本技能・所作・マナーを修めた、最初の到達段階。ホスピタリティの土台を体系的に身につけます。' },
    { grade: 'Professional', ja: 'プロフェッショナル・バトラー', img: '/images/generated/jba-interior-suits-02.webp', alt: '執事の正装が並ぶワードローブ', desc: '実務の現場で主人の暮らしとビジネスを支える、専門技能を備えた段階。食事・接遇・出張手配などの実践力を証明します。' },
    { grade: 'Master', ja: 'マスター・バトラー', img: '/images/generated/jba-still-roses-01.webp', alt: '薔薇と画架のあるサロンの一隅', desc: '家全体の運営を統括し、後進の指導にもあたる最上位。約3年の育成期間を経た、質の高い人材の頂点に位置します。' },
  ];

  // [A5][A6] 学びを社会へ広げる（三列カード）
  const outreach = [
    { en: 'Seminars', ja: 'セミナー・イベント', img: '/images/generated/jba-fs-002-about_activities.webp', alt: '執事によるビジネスマナー研修のイメージ', desc: 'リーダーシップ・ホスピタリティ・コミュニケーションを主題に、企業や個人へ実践的なセミナーを開催。執事の所作と考え方をビジネスの現場へ届けます。' },
    { en: 'International', ja: '国際交流', img: '/images/generated/jba-fs-003-about_activities.webp', alt: '国際的な執事ネットワークを象徴する情景', desc: 'グローバルな視野で執事文化の発展に貢献するため、国際的に通用する執事の育成に取り組みます。海外からのお客様への対応力も重視しています。' },
    { en: 'Publishing', ja: '出版・情報発信', img: '/images/generated/jba-fs-004-about_activities.webp', alt: '執事文化を伝える出版・情報発信のイメージ', desc: '専門刊行物や書籍の発行に加え、YouTube「執事ベル」を通じた発信により、執事文化の「いま」を社会へ広く伝えます。' },
  ];

  // [A2][A3] 信頼と品質を支える仕組み（紺パネル・数値創作なし）
  const trustPillars = [
    { en: 'Confidentiality', ja: '双方向の守秘契約', desc: '協会・執事・お客様の三者間で双方向のNDA（守秘義務契約）を締結。暮らしの最も内側に触れる仕事だからこそ、秘密は固く守られます。' },
    { en: 'Screening', ja: '双方への反社会的勢力チェック', desc: '執事・お客様の双方に反社会的勢力チェックを徹底。厳しいデューデリジェンスにも耐えうるコンプライアンス水準を維持しています。' },
    { en: 'Accountability', ja: '派遣ではない責任構造', desc: '労働者派遣ではなく、協会が主体となる業務委託・ご紹介ベースの運営。品質管理からトラブル対応まで、協会が間に立って責任を持ちます。' },
  ];

  return (
    <Layout>
      {/* SEO は updateSEO(seoData['/about/activities']) を単一の正とする（JSX 二重定義は除去・H3） */}
      {/* ===== ヒーロー — 全幅ビルボード型（画像+紺オーバーレイ+左寄せテキスト）。
           由来: home / overview ヒーローの全幅ビルボード文法 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-fs-005-about_activities-v2.webp"
          alt="執事の育成と実践への取り組みを表すイメージ"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Our Activities</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">育て、認め、広げ、</span>
              <span className="block">そして守る</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会は、執事の専門性を高め、その文化を次の世代へ継承するために活動しています。人を育てる研修、技能を証明する資格認定、学びを社会へ広げる発信、そして品質を静かに支える仕組みが、私たちの日々を形づくっています。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/about/activities" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* --- I. 活動の全体像（大フォント一覧テーブル型）= 新バリエーション①
                   由来注記: home VII「会員種別の細罫テーブル調」の罫線文法を、活動名を大きく組む
                   1カラムの一覧テーブルへ翻案。overviewの定義リスト（15px値）とは異なり、活動名を22〜26px級で
                   階層化し、可読性を最優先にした「先出し一覧」。トップにこの大組み一覧型は無いため新造。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Overview</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  協会の活動を、<span className="text-[#a38e65]">4つの柱</span>で
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  <p>
                    私たちの活動は、大きく4つの柱で構成されています。第一に人を育てる研修、第二にその技能を客観的に証明する資格認定、第三に学びを社会へ広げる発信、そして第四に、それらすべてを静かに支える信頼と品質の仕組みです。
                  </p>
                  <p>
                    これらは独立した取り組みではなく、互いに連携しながら一つの循環を描いています。育てた人材の技能を資格で保証し、その学びをセミナーや出版で社会へ還元し、守秘と審査の体制がすべての土台を支える——その全体像を、まず一覧でご覧ください。
                  </p>
                </div>
                {/* 大フォント一覧テーブル（活動名22px級・細罫整列・縦ライン揃え） */}
                <div className="border-t-2 border-[#1a2942]">
                  {activityPillars.map((p) => (
                    <a
                      key={p.no}
                      href={p.anchor}
                      className="group flex items-baseline gap-4 sm:gap-7 border-b border-[#e2ddd2] py-6 hover:bg-[#f8f6f0] transition-colors duration-300 -mx-2 px-2"
                    >
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none shrink-0 w-9 sm:w-12">{p.no}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block jba-display font-medium text-[#1a2942] text-xl sm:text-2xl lg:text-[26px] leading-[1.4] tracking-[0.02em] group-hover:text-[#a38e65] transition-colors duration-300">{p.name}</span>
                        <span className="block font-['Noto_Serif_JP',serif] text-sm sm:text-[15px] text-[#4a4638] leading-[1.85] mt-1.5">{p.lead}</span>
                      </span>
                      <span className="hidden sm:block font-['Playfair_Display',serif] text-[#8f7a56] text-xl shrink-0 self-center transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </a>
                  ))}
                </div>
              </section>

              {/* --- II. 人材育成と研修（分割エディトリアル型）
                   由来: 共通カタログ N02 split editorial（画像+テキスト50/50・左寄せ）を2カラムへ適用。 --- */}
              <section id="training" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Training</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  人を育てることから、すべては始まる
                </h2>
                <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
                  <div>
                    <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                      <p>
                        執事の基本技能から専門技術まで、体系的な研修プログラムを通じて質の高い執事を育成します。理論と実践を組み合わせ、所作・マナー・ホスピタリティの土台から、食事や接遇といった専門領域までを段階的に学びます。
                      </p>
                      <p>
                        学びの場は、お台場に構える事務所兼「執事サロン」です。中世ヨーロッパの古城の一室を再現したこの拠点を実地研鑽の場とし、伝統を次世代へと継承しています。約3年の育成期間を経て、質の高い人材が現場へと巣立っていきます。
                      </p>
                    </div>
                    {/* 研修領域の細罫リスト */}
                    <dl className="mt-8 border-t border-[#e2ddd2]">
                      {[
                        { t: '基礎の修得', d: '所作・マナー・プロトコル・コミュニケーションの土台' },
                        { t: '専門技術', d: '食事の給仕・接遇・出張手配など実務の専門領域' },
                        { t: '実地研鑽', d: 'お台場の執事サロンでの実践的な訓練' },
                      ].map((row) => (
                        <div key={row.t} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                          <dt className="sm:w-[112px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em] pt-0.5">{row.t}</dt>
                          <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">{row.d}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <img
                    src="/images/generated/jba-hands-notebook-03.webp"
                    alt="白手袋で記録をとる執事の手元のイメージ"
                    className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30"
                  />
                </div>
              </section>

              {/* --- III. 資格認定制度（積層splitカード型）
                   由来: 共通カタログ N04 積層splitカード（画像0.4/テキスト0.6）。資格名を大フォントで階層化し可読性を確保。 --- */}
              <section id="certification" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Certification</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  技能を、<span className="text-[#a38e65]">三段階</span>で証明する
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  執事としての専門知識と技能を客観的に評価し、認定する独自の資格制度を運営しています。アソシエイト・プロフェッショナル・マスターの三段階を軸に、技能を段階的に証明することで、サービスの質を担保します。
                </p>
                <div className="space-y-6">
                  {certifications.map((c) => (
                    <div key={c.grade} className="grid sm:grid-cols-[0.4fr_0.6fr] border border-[#e2ddd2] overflow-hidden bg-white">
                      <img src={c.img} alt={c.alt} className="w-full h-52 sm:h-full min-h-[200px] object-cover object-top" />
                      <div className="p-7 sm:p-8 flex flex-col justify-center">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.08em] mb-1.5">{c.grade} Butler</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl lg:text-[26px] leading-[1.4] mb-3">{c.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{c.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- IV. 学びを社会へ広げる（三列カードグリッド型）
                   由来: 共通カタログ N05 三列カードグリッド（中央見出し）。各カードに画像を必須配置。 --- */}
              <section id="outreach" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Outreach</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  学びを、協会の外へ
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  執事の技能と考え方は、協会の内側にとどめておくものではありません。セミナーで企業や個人へ、国際交流で世界へ、出版と発信で社会全体へ——三つの経路で、執事文化の価値を広く届けています。
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
                  {outreach.map((o) => (
                    <div key={o.ja} className="border border-[#e2ddd2] overflow-hidden bg-white flex flex-col">
                      <img src={o.img} alt={o.alt} className="w-full h-48 object-cover object-top" />
                      <div className="p-6 flex-1 flex flex-col">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1.5">{o.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{o.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{o.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- V. 信頼と品質を支える仕組み（紺「約束」パネル型）= 新バリエーション②
                   由来注記: 共通カタログの sec-stats（紺4分割・数値カウンタ）の紺地・分割構図を、
                   数値を創作せず「守秘・審査・責任」の体制説明へ翻案した「約束パネル」。
                   overviewの引用大組み（blockquote主役）とは構成が異なる（値の並置が主役）。トップに体制並置型は無いため新造。 --- */}
              <section id="trust" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Trust & Quality</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  安心して、お任せいただくために
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-14 py-11 sm:py-13 lg:py-14">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-10">
                    執事は、家庭やビジネスの最も内側に触れる仕事です。だからこそ、華やかなサービスの裏側で、守秘・審査・責任の三つの仕組みを徹底しています。これらが揃ってはじめて、安心してお任せいただける関係が成り立ちます。
                  </p>
                  <div className="grid sm:grid-cols-3 gap-8 lg:gap-10 border-t border-[#a38e65]/40 pt-10">
                    {trustPillars.map((tp) => (
                      <div key={tp.en}>
                        <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">{tp.en}</div>
                        <h3 className="jba-display font-medium text-[#f8f6f0] text-lg lg:text-xl mb-3 leading-[1.5]">{tp.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">{tp.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* --- VI. 導線（紺CTAパネル型）。由来: home / overview 締めCTAパネル --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  活動に触れてみる
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    資格制度の詳細や、企業・個人向けのセミナーなど、協会の活動は多くの入口から触れていただけます。ご関心のあるテーマから、まずはお気軽にご覧ください。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
                    <a href="/seminars" className={BTN08_ON_NAVY}>セミナー・研修を見る</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
